import { Image as ImageIcon, Trash, X } from "lucide-react";
import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { handleStopPropagation } from "./Utils";
import { useDispatch } from "react-redux";
import { showAlert } from "store/reducers/alertReducer";
import { ErrorMessage } from "formik";

const FileUpload = ({
  uploadedFiles,
  setUploadedFiles,
  maxFiles = 3,
  maxFileSize = 5 * 1024 * 1024, // 5 MB
  acceptedFileTypes = ["image/png", "image/jpeg"], //"image/svg+xml"
  requireSquare = false,
  requireRectangle = false,
  onChange = () => {},
  className = "",
  isShowErrorMsg = false,
  name = "",
}) => {
  const dispatch = useDispatch();

  const formatAcceptedTypes = () => {
    const result = {};
    acceptedFileTypes?.forEach((type) => {
      result[type] = [];
    });
    return result;
  };

  const validateDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const { width, height } = img;
        URL.revokeObjectURL(img.src);
        if (requireSquare && width !== height) {
          reject("Image must be square.");
        } else if (requireRectangle && width === height) {
          reject("Image must be rectangular.");
        } else {
          resolve();
        }
      };
      img.onerror = () => reject("Failed to load image.");
    });

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      // Handle rejected files due to dropzone restrictions
      rejectedFiles.forEach((file) => {
        const errorMessage = file.errors
          .map((e) =>
            e.code === "file-too-large"
              ? `Please upload a file that is less than ${(maxFileSize / 1024).toFixed(0)} KB in size`
              : e.message
          )
          .join(", ");

        dispatch(
          showAlert({
            text: errorMessage,
            type: "error",
          })
        );
      });

      // Validate accepted files
      const newFiles = [];
      for (const file of acceptedFiles) {
        try {
          await validateDimensions(file); // Validate square/rectangle based on props
          const previewUrl = URL.createObjectURL(file);
          newFiles.push(file);
        } catch (error) {
          // alert(`File rejected: ${file.name} (${error})`);
          dispatch(
            showAlert({
              text: `File rejected: ${file.name} (${error})`,
              type: "error",
            })
          );
        }
      }

      // Check max file limit
      if (uploadedFiles.length + newFiles.length > maxFiles) {
        // alert(`You can only upload a maximum of ${maxFiles} images.`);
        dispatch(
          showAlert({
            text: `You can only upload a maximum of ${maxFiles} images.`,
            type: "error",
          })
        );
        return;
      }

      const updatedFiles = [...uploadedFiles, ...newFiles];
      setUploadedFiles(updatedFiles);
      if (onChange) {
        onChange(updatedFiles.map(({ file }) => file)); // Pass raw files to the parent
      }
    },
    [uploadedFiles, maxFiles, requireSquare, requireRectangle, onChange]
  );

  const removeFile = (index) => {
    const fileToRemove = uploadedFiles[index];
    if (fileToRemove?.previewUrl) {
      URL.revokeObjectURL(fileToRemove.previewUrl); // Clean up the preview URL
    }
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
    if (onChange) {
      onChange(updatedFiles.map(({ file }) => file));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: formatAcceptedTypes(),
    maxSize: maxFileSize,
    multiple: true,
  });

  return (
    <div className="mb_20">
      {/* Drag-and-Drop Area */}
      <div
        {...getRootProps()}
        className={`file_upload g_between ${className}`}
        style={{
          background: isDragActive ? "#f0f0f0" : "#ffffff",
        }}
      >
        <input {...getInputProps()} />

        {/* Display Uploaded Images */}
        {!uploadedFiles?.filter(Boolean).length ? (
          <div className="d-flex flex-column w-100 align-items-center">
            <ImageIcon width={32} height={32} className="text_gray mb-2" />
            <p className="w-100">
              <span>Upload a file</span> or drag and drop
              <br />
              (Max {maxFiles} files, max {maxFileSize / (1024 * 1024)} MB per
              file)
            </p>
          </div>
        ) : (
          <div className="d-flex align-items-end">
            <div className="uploaded_images" onClick={handleStopPropagation}>
              {uploadedFiles?.map((item, index) => {
                let previewUrl = "";

                // If the item is a file, create a preview URL
                if (item instanceof File) {
                  previewUrl = URL.createObjectURL(item);
                } else if (typeof item === "string") {
                  // If it's an image URL, use it directly
                  previewUrl = item;
                }

                return (
                  <div key={index} className="position-relative single_img">
                    <img
                      src={previewUrl} // Use either the generated URL or the provided image URL
                      alt={`Uploaded Preview ${index + 1}`}
                      className="uploaded_image"
                    />

                    <X
                      className="trash_icon"
                      onClick={() => removeFile(index)}
                    />
                  </div>
                );
              })}
            </div>
            {uploadedFiles.length !== maxFiles && (
              <button type="button" className="upload_btn">
                +
              </button>
            )}
          </div>
        )}
      </div>
      {isShowErrorMsg && (
        <ErrorMessage name={name} component="div" className="text-danger" />
      )}
    </div>
  );
};

export default FileUpload;
